import { Moment } from 'moment'

import { ConfigFilterItem, ConfigItem } from 'Core/interfaces/filters'

import { ItemsTableFields } from '../Pages/ServiceRequest/Edit/Complete/types'

import { Item } from './Item'

export enum QuantityCodeId {
	ONE = 1,
	SAME_AS_PARENT_ID = 2
}

export interface AssigneeSearchResult {
	searchById: Entity[]
	searchByName: Entity[]
}

export enum ServiceRequestFormType {
	new,
	existing
}

export enum ServiceFields {
	itemId = 'itemId',
	binRemovalFlag = 'binRemovalFlag',
	estimatedQuantity = 'estimatedQuantity',
	keyName = 'keyName',
	uom = 'uom',
	priceType = 'priceType',
	itemPrice = 'itemPrice',
	itemCost = 'itemCost',
	supplierAccountNumber = 'supplierAccountNumber',
	parentItemId = 'parentItemId',
	isPrebill = 'isPrebill',
	isDependent = 'isDependent',
	isScale = 'isScale',
	quantityCodeId = 'quantityCodeId',
	itemPriceData = 'itemPriceData',
	supplierCostData = 'supplierCostData',
	newItemId = 'newItemId',
	newParentItemId = 'newParentItemId',
	children = 'children',
	isUpdatedField = 'isUpdatedField',
	isAutoCharge = 'isAutoCharge',
	newServiceDescription = 'newServiceDescription',
	serviceRequestDraftItemId = 'serviceRequestDraftItemId'
}

export enum NewServiceFields {
	serviceDescription = 'serviceDescription',
	expectedQuantity = 'expectedQuantity',
	expectedServiceTypeId = 'expectedServiceTypeId',
	expectedProductCategoryId = 'expectedProductCategoryId',
	expectedUnitOfMeasureId = 'expectedUnitOfMeasureId',
	keyName = 'keyName'
}

export enum PriceType {
	price = 'Price',
	minimumCharge = 'Minimum Charge',
	scale = 'Scale',
	formula = 'Formula'
}

export enum EmailType {
	customer,
	supplier
}

export enum ServiceRequestStatuses {
	PendingSupplierResponse = 'Pending Supplier Response',
	PendingCustomerResponse = 'Pending Customer Response',
	Approved = 'Approved',
	PendingSupplierResponseException = 'Pending Supplier Response Exception',
	PartialCompleted = 'Partially Completed',
	ServiceCompleted = 'Service Completed',
	Cancelled = 'Cancelled',
	PendingItemSetup = 'Pending Item Setup',
	NewItemRequest = 'New Item Request'
}

export enum ServiceRequestSearchSuggestionFields {
	searchByTrackingNumber = 'searchByTrackingNumber',
	searchByServiceSiteId = 'searchByServiceSiteId',
	searchByServiceSiteName = 'searchByServiceSiteName',
	searchByCompanyId = 'searchByCompanyId',
	searchByCompanyName = 'searchByCompanyName',
	searchBySupplierLocationId = 'searchBySupplierLocationId',
	searchBySupplierLocationName = 'searchBySupplierLocationName'
}

export enum ServiceRequestFilterFields {
	requestDateFrom = 'requestDateFrom',
	requestDateTo = 'requestDateTo',
	assignedToId = 'assignedToId',
	serviceRequestStatusId = 'serviceRequestStatusId',
	incomingMethodId = 'incomingMethodId',
	serviceTypeId = 'serviceTypeId'
}

export interface ServiceRequestParentItemIds {
	itemId: BasicNumber
	index: number
}

export enum ServiceRequestItemStatus {
	pending = 'pending',
	completed = 'completed',
	cancelled = 'cancelled'
}

export interface MissingPriceCostConfig {
	isMissing: boolean
	itemId: BasicNumber
}

export declare namespace ServiceRequest {
	interface Basic {
		trackingNumber: string
		id: number
		serviceRequestStatus: { id: number } & NameCode
		createDate: string
		createdBy: string
		lastUpdateDate: string
		lastUpdatedBy: string
		assigneeContact: Contact
		incomingMethod: Entity
		serviceType: number
		company: Entity
		serviceSite: Entity
		isTurkishServiceSite: boolean
		supplierLocation: Entity
		author: Contact
	}

	interface Address {
		addressLine1: BasicString
		addressLine2: BasicString
		city: BasicString
		state: BasicString
		zipCode: BasicString
	}

	namespace Edit {
		interface ServiceRequestItem {
			id: number
			itemId: number
			item: Entity & { estimatedPounds: number }
			parentItemId: BasicNumber
			immediateParentId: BasicNumber
			serviceRequestItemStatus: {
				id: number
				name: ServiceRequestItemStatus
				code: BasicString
			}
			itemPrice: Item.ServiceRequest.ItemPrice
			itemCost: Item.ServiceRequest.SupplierCost
			supplierAccountNumber: BasicString
			estimatedQuantity: BasicNumber
			binRemovalFlag: boolean
			actualServiceDate: BasicString
			serviceOccurrenceId: BasicNumber
			customerOrderId: BasicNumber
			bolNumber: BasicString
			supplierTransactionCode: BasicString
			actualQuantity: BasicNumber
			kilograms: BasicNumber
			pounds: BasicNumber
			disposalFacility: Entity
			createDate: string
			createdBy: BasicString
			lastUpdateDate: BasicString
			lastUpdatedBy: BasicString
			isDisposalOrRecycling: boolean
			children: ServiceRequestItem[]
			quantityCodeId: BasicNumber
			isAutoCharge: boolean
		}

		interface ServiceRequestDraftItems {
			createDate: BasicString
			createdBy: BasicString
			expectedProductCategory: NullableEntity
			expectedQuantity: BasicNumber
			expectedServiceType: CodeEntity | null
			expectedUnitOfMeasure: NullableEntity
			id: number
			lastUpdateDate: BasicString
			lastUpdatedBy: BasicString
			serviceDescription: BasicString
		}

		interface Service {
			id: number
			itemId: number
			item: Entity
			status: Entity
			actualQuantity: BasicNumber
			actualServiceDate: BasicString
			estimatedQuantity: BasicString
			serviceOccurrenceId: BasicNumber
			customerOrderId: BasicNumber
			uom: BasicString
			priceType: PriceType | null
			itemPrice: BasicString
			itemCost: BasicString
			supplierAccountNumber: BasicString
			binRemovalFlag: boolean
			parentItemId: BasicNumber
			itemPriceDetails: Item.ServiceRequest.ItemPrice
			itemCostDetails: Item.ServiceRequest.SupplierCost
			quantityCodeId: BasicNumber
			isAutoCharge: boolean
			children: Service[]
		}
		interface Basic {
			trackingNumber: string
			id: number
			serviceRequestStatus: IdCodeName
			createDate: BasicString
			createdBy: BasicString
			lastUpdateDate: BasicString
			lastUpdatedBy: BasicString
			assigneeContact: Contact
			incomingMethod: Entity
			serviceType: BasicNumber
			company: Entity
			serviceSite: Entity
			isTurkishServiceSite: boolean
			supplierLocation: Entity
			author: Contact
			supplierConfirmationNumber: BasicString
			estimatedServiceDateFrom: BasicString
			estimatedServiceDateTo: BasicString
			proposedEstimatedServiceDateFrom: BasicString
			proposedEstimatedServiceDateTo: BasicString
			serviceSiteAddress: Address | null
			serviceSiteContact: Contact
			customerNotificationSentFlag: boolean
			serviceRequestSentEmails: {
				email: BasicString
				serviceRequestSentEmailType: BasicNumber
			}[]
			supplier: Entity
			supplierLocationContact: Contact
			supplierLocationAddress: Address
			supplierNotificationSentFlag: boolean
			specialInstructions: BasicString
			serviceRequestItems: ServiceRequestItem[]
			serviceRequestItemsAsHierarchy: ServiceRequestItem[]
			serviceRequestDraftItems: ServiceRequestDraftItems[]
			canApprove: boolean
			canCancel: boolean
			canComplete: boolean
			canModify: boolean
			newDatesSuggestedByCustomer: boolean
			newDatesSuggestedBySupplier: boolean
			salesforceId: BasicString
		}
	}

	interface ServiceRequestItem {
		[ServiceFields.newServiceDescription]?: BasicString
		[ServiceFields.serviceRequestDraftItemId]?: BasicNumber
		[ServiceFields.itemId]: BasicNumber
		[ServiceFields.binRemovalFlag]: boolean
		[ServiceFields.estimatedQuantity]: BasicString
		[ServiceFields.uom]: BasicString
		[ServiceFields.itemPrice]: BasicString
		[ServiceFields.priceType]: PriceType | null
		[ServiceFields.itemCost]: BasicString
		[ServiceFields.supplierAccountNumber]: BasicString
		[ServiceFields.isPrebill]: boolean
		[ServiceFields.isScale]: boolean
		[ServiceFields.isDependent]: boolean
		[ServiceFields.parentItemId]: BasicNumber
		[ServiceFields.quantityCodeId]: BasicNumber
		[ServiceFields.itemPriceData]: Item.ServiceRequest.ItemPrice | null
		[ServiceFields.supplierCostData]: Item.ServiceRequest.SupplierCost | null
		[ServiceFields.newItemId]: BasicString
		[ServiceFields.newParentItemId]: BasicString
		[ServiceFields.children]: ServiceRequestItem[]
		[ServiceFields.isUpdatedField]: boolean
		[ServiceFields.isAutoCharge]: boolean
	}

	interface TreeServiceRequestItems extends ServiceRequestItem {
		children: TreeServiceRequestItems[]
	}

	interface NewServiceRequestItem {
		[NewServiceFields.serviceDescription]: BasicString
		[NewServiceFields.expectedQuantity]: BasicNumber
		[NewServiceFields.expectedServiceTypeId]: BasicNumber
		[NewServiceFields.expectedProductCategoryId]: BasicNumber
		[NewServiceFields.expectedUnitOfMeasureId]: BasicNumber
	}

	interface PhysicalAddress {
		id: number
		addressTypeId: number
		addressType: string
		addressLine1: string
		addressLine2: string
		city: string
		stateProvinceId: number
		state: string
		zipCode: string
		contact: string
		contactId: number
		locality1: Entity
		locality2: Entity
		locality3: Entity
		latitude: number
		longitude: number
		usTaxRate: number
	}

	interface Contact extends Entity {
		email: BasicString
		phone: BasicString
	}

	interface ServiceSite {
		id: number
		name: string
		isTurkish: boolean
		physicalAddress: PhysicalAddress
		contacts: Contact[]
	}

	interface SupplierLocationDetails {
		id: number
		name: string
		physicalAddress: PhysicalAddress
		contacts: Contact[]
		supplier: Entity
	}

	namespace Form {
		namespace CustomerDetails {
			namespace Form {
				interface Add {
					assigneeContactId: LabelValueExtra<number> | null
					incomingMethodId: BasicNumber
					serviceSiteId: BasicNumber
					companyId: LabelValueExtra<number> | null
					serviceSiteContactId: BasicNumber
				}
			}
		}

		namespace ServiceDetails {
			namespace Form {
				interface Add {
					serviceType: ServiceRequestFormType
					existing: {
						estimatedServiceDateFrom: Moment | null
						estimatedServiceDateTo: Moment | null
						supplierId: LabelValueExtra<number> | null
						supplierLocationId: LabelValueExtra<number> | null
						supplierLocationContactId: BasicNumber
						specialInstructions: BasicString
						serviceRequestItems: {
							[ServiceFields.serviceRequestDraftItemId]?: BasicNumber
							[ServiceFields.newServiceDescription]?: BasicString
							[ServiceFields.itemId]: BasicNumber
							[ServiceFields.binRemovalFlag]: boolean
							[ServiceFields.estimatedQuantity]: BasicString
							[ServiceFields.uom]: BasicString
							[ServiceFields.itemPrice]: BasicString
							[ServiceFields.priceType]: PriceType | null
							[ServiceFields.itemCost]: BasicString
							[ServiceFields.supplierAccountNumber]: BasicString
							[ServiceFields.isPrebill]: boolean
							[ServiceFields.isScale]: boolean
							[ServiceFields.isDependent]: boolean
							[ServiceFields.parentItemId]: BasicNumber
							[ServiceFields.quantityCodeId]: BasicNumber
							[ServiceFields.itemPriceData]: Item.ServiceRequest.ItemPrice | null
							[ServiceFields.supplierCostData]: Item.ServiceRequest.SupplierCost | null
							[ServiceFields.newItemId]: BasicString
							[ServiceFields.newParentItemId]: BasicString
							[ServiceFields.isUpdatedField]: boolean
							[ServiceFields.children]: ServiceRequest.ServiceRequestItem[]
							[ServiceFields.isAutoCharge]: boolean
						}[]
					}
					new: {
						estimatedServiceDateFrom: Moment | null
						estimatedServiceDateTo: Moment | null
						serviceRequestItems: {
							[NewServiceFields.serviceDescription]: BasicString
							[NewServiceFields.expectedQuantity]: BasicNumber
							[NewServiceFields.expectedServiceTypeId]: BasicNumber
							[NewServiceFields.expectedProductCategoryId]: BasicNumber
							[NewServiceFields.expectedUnitOfMeasureId]: BasicNumber
						}[]
						specialInstructions: BasicString
					}
				}
			}
		}

		namespace Service {
			namespace Existing {
				type FieldType = Record<
					ServiceFields,
					Key | null | boolean | ServiceRequest.Form.Service.Existing.FieldType[]
				>

				interface TableConfig<
					SelectFieldNames extends string,
					InputFieldNames extends string,
					SwitchFieldNames extends string
				> {
					selects: Record<
						SelectFieldNames,
						{
							options: LabelValue<number>[]
						}
					>
					inputs: Record<InputFieldNames, {}>
					switchs: Record<SwitchFieldNames, {}>
				}
				namespace Form {
					interface Add {
						serviceRequestItems: ServiceRequestItem[]
					}
				}
			}
			namespace New {
				type FieldType = Record<NewServiceFields, Key | null | boolean>

				interface TableConfig<
					SelectFieldNames extends string,
					InputFieldNames extends string
				> {
					selects: Record<
						SelectFieldNames,
						{
							options: LabelValue<number>[]
							placeholder: string
						}
					>
					inputs: Record<
						InputFieldNames,
						{
							isDefaultInput: boolean
							maxLength?: number
							placeholder: string
						}
					>
				}
				namespace Form {
					interface Add {
						serviceRequestItems: NewServiceRequestItem[]
					}
				}
			}
		}

		namespace SendEmail {
			namespace Form {
				interface Add {
					serviceRequestId: BasicNumber
					emailType: BasicNumber
					customerEmails: string[]
					supplierEmails: string[]
					customerEmailText: BasicString
					supplierEmailText: BasicString
					isSupplierEmail: boolean
					isCustomerEmail: boolean
				}
			}
			namespace Request {
				interface Add {
					serviceRequestId: number
					emailType: number
					emails: string[]
					emailText: BasicString
				}
			}
		}
	}

	namespace Request {
		namespace Existing {
			interface ServiceRequestItem {
				itemId: BasicNumber
				binRemovalFlag: boolean
				estimatedQuantity: BasicString
				quantityCodeId: BasicNumber
				children: ServiceRequestItem[]
				isAutoCharge?: boolean
				serviceRequestDraftItemId?: BasicNumber
			}
			interface Add {
				assigneeContactId: BasicNumber
				incomingMethodId: BasicNumber
				serviceType: BasicNumber
				companyId: BasicNumber
				serviceSiteId: BasicNumber
				serviceSiteContactId: BasicNumber
				estimatedServiceDateFrom: BasicString
				estimatedServiceDateTo: BasicString
				supplierId: BasicNumber
				supplierLocationId: BasicNumber
				supplierLocationContactId: BasicNumber
				specialInstructions: BasicString
				serviceRequestItems: ServiceRequestItem[]
			}
		}

		namespace New {
			interface Add {
				assigneeContactId: BasicNumber
				incomingMethodId: BasicNumber
				serviceType: BasicNumber
				companyId: BasicNumber
				serviceSiteId: BasicNumber
				serviceSiteContactId: BasicNumber
				estimatedServiceDateFrom: BasicString
				estimatedServiceDateTo: BasicString
				specialInstructions: BasicString
				serviceRequestDraftItems: {
					serviceDescription: BasicString
					expectedQuantity: BasicNumber
					expectedServiceTypeId: BasicNumber
					expectedProductCategoryId: BasicNumber
					expectedUnitOfMeasureId: BasicNumber
				}[]
			}
		}
	}

	namespace Search {
		interface Result {
			[ServiceRequestSearchSuggestionFields.searchByCompanyId]: Entity[]
			[ServiceRequestSearchSuggestionFields.searchByCompanyName]: Entity[]
			[ServiceRequestSearchSuggestionFields.searchByServiceSiteId]: Entity[]
			[ServiceRequestSearchSuggestionFields.searchByServiceSiteName]: Entity[]
			[ServiceRequestSearchSuggestionFields.searchBySupplierLocationId]: Entity[]
			[ServiceRequestSearchSuggestionFields.searchBySupplierLocationName]: Entity[]
			[ServiceRequestSearchSuggestionFields.searchByTrackingNumber]: Entity[]
		}

		interface Config {
			[ServiceRequestSearchSuggestionFields.searchByCompanyId]: ConfigItem<ServiceRequestSearchSuggestionFields>
			[ServiceRequestSearchSuggestionFields.searchByCompanyName]: ConfigItem<ServiceRequestSearchSuggestionFields>
			[ServiceRequestSearchSuggestionFields.searchByServiceSiteId]: ConfigItem<ServiceRequestSearchSuggestionFields>
			[ServiceRequestSearchSuggestionFields.searchByServiceSiteName]: ConfigItem<ServiceRequestSearchSuggestionFields>
			[ServiceRequestSearchSuggestionFields.searchBySupplierLocationId]: ConfigItem<ServiceRequestSearchSuggestionFields>
			[ServiceRequestSearchSuggestionFields.searchBySupplierLocationName]: ConfigItem<ServiceRequestSearchSuggestionFields>
			[ServiceRequestSearchSuggestionFields.searchByTrackingNumber]: ConfigItem<ServiceRequestSearchSuggestionFields>
		}
	}

	namespace Filters {
		interface Config {
			[ServiceRequestFilterFields.requestDateFrom]: ConfigFilterItem<string, null>
			[ServiceRequestFilterFields.requestDateTo]: ConfigFilterItem<string, null>
			[ServiceRequestFilterFields.assignedToId]: ConfigFilterItem<string, string>
			[ServiceRequestFilterFields.serviceRequestStatusId]: ConfigFilterItem<string, string>
			[ServiceRequestFilterFields.serviceTypeId]: ConfigFilterItem<string, string>
			[ServiceRequestFilterFields.incomingMethodId]: ConfigFilterItem<string, string>
		}
	}

	namespace Approve {
		interface Form {
			actions: number[]
			supplierConfirmationNumber: BasicString
		}

		interface Request extends Approve.Form {}
	}

	namespace Modify {
		interface Form {
			actions: number[]
			proposedServiceRequestFrom: Moment | null
			proposedServiceRequestTo: Moment | null
			reason: BasicString
			serviceRequestItems: Array<
				Edit.Service & {
					rootLevel: number
				}
			>
			customerEmails: string[]
			supplierEmails: string[]
			customerEmailText: BasicString
			supplierEmailText: BasicString
			isSupplierEmail: boolean
			isCustomerEmail: boolean
			serviceRequestId: BasicNumber
			emailType: EmailType | null
		}

		interface Request {
			actions: number[]
			reason: BasicString
			proposedServiceRequestFrom: BasicString
			proposedServiceRequestTo: BasicString
			serviceRequestItems: {
				serviceRequestItemId: number
				binRemovalFlag: boolean
				estimatedQuantity: number
			}[]
		}
	}

	namespace Complete {
		type TableItem = Record<
			ItemsTableFields,
			Entity | Primitives | Nullish | LabelValue<number> | Moment | ServiceRequestItemStatus
		>
		interface Form {
			actions: number[]
			serviceRequestToCompleteItems: TableItem[]
		}

		interface Request {
			actions: number[]
			serviceRequestToCompleteItems: {
				serviceRequestItemId: number
				actualServiceDate: string
				actualQuantity: number
				kilograms: BasicNumber
				pounds: BasicNumber
				bolNumber: string
				supplierTransactionCode: string
				disposalFacilityId: BasicNumber
				expectSupplierInvoice: boolean
			}[]
		}
	}
	namespace Cancel {
		interface Form {
			actions: number[]
			reason: BasicString
		}
		interface Request {
			actions: number[]
			reason: string
		}
	}
	namespace Proceed {
		interface Form {
			assigneeId: BasicString
			salesforceId: BasicString
			notes: BasicString
		}
		interface Request {
			assigneeId: BasicNumber
			salesforceId: BasicString
			reason: BasicString
		}
	}
}
